import { useMemo, useState } from 'react';

import { useUpdateUser, useUsers } from '@/api/queries';
import type { UserAccount } from '@/api/types';
import { useSession } from '@/session/SessionContext';
import { useClientPaging } from '@/ui/useClientPaging';

import { nextSort, type SortKey, type UserSort, visibleUsers } from './usersList';

export type UserDialogState =
  | { kind: 'add' }
  | { kind: 'edit'; user: UserAccount }
  | { kind: 'password'; user: UserAccount }
  | { kind: 'deactivate'; user: UserAccount };

/** The Users tab's permissions, filters, sorting, paging and open dialog. */
export function useUsersTab() {
  const users = useUsers();
  const update = useUpdateUser();
  const { user: caller, can } = useSession();
  const canReset = can('user.password.reset');
  const canEdit = can('user.update', 'user.role.change', 'user.source.change');
  const perms = {
    canCreate: can('user.create'),
    // Only an Admin changes an Admin account; the rows say so instead of failing.
    callerIsAdmin: caller?.roles.includes('admin') === true,
    canStatus: can('user.status.change'),
    canReset,
    canEdit,
    withActions: canReset || canEdit,
  };

  const [search, setSearch] = useState('');
  const [sources, setSources] = useState<string[]>([]);
  const [roles, setRoles] = useState<string[]>([]);
  // Opens on active accounts; clearing the filter shows deactivated ones too.
  const [statuses, setStatuses] = useState<string[]>(['active']);
  const [sort, setSort] = useState<UserSort>({ key: 'display_name', order: 'asc' });
  const [dialog, setDialog] = useState<UserDialogState | null>(null);

  const rows = useMemo(() => users.data?.users ?? [], [users.data]);
  const visible = useMemo(
    () => visibleUsers(rows, { search, sources, roles, statuses }, sort),
    [rows, search, sources, roles, statuses, sort],
  );
  const { page, setPage, pageSize, setPageSize, pageRows } = useClientPaging(visible, [search, sources, roles, statuses, sort]);

  function onSort(key: string) {
    setSort((s) => nextSort(s, key as SortKey));
  }

  /** Switching off asks first (it signs the account out); switching on is immediate. */
  function onToggleActive(u: UserAccount, active: boolean) {
    if (active) update.mutate({ id: u.id, change: { is_active: true } });
    else setDialog({ kind: 'deactivate', user: u });
  }

  return {
    users,
    update,
    callerId: caller?.id,
    perms,
    filters: { search, setSearch, sources, setSources, roles, setRoles, statuses, setStatuses },
    rows,
    visible,
    pageRows,
    sort,
    onSort,
    page,
    setPage,
    pageSize,
    setPageSize,
    dialog,
    setDialog,
    onToggleActive,
  };
}

export type UsersTabState = ReturnType<typeof useUsersTab>;
export type UserPerms = UsersTabState['perms'];
